import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import {
    AdjuvanteGewichtsreducerendeMedicatieOpties,
    FollowUpStatusOpties,
    TabakOpties,
    TherapeutischeMedicamenteuzeVeranderingOpties,
} from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { hba1cSchema } from '@common/validation/schemas';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type {
    BariPostNurseSpecialistFollowUpComorbiditeitenDataV2,
    BariPostNurseSpecialistFollowUpDataV2,
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataV2,
} from './bariPostNurseSpecialistFollowUpDataV2';
import {
    bariPostNurseSpecialistFollowUpBeoordelingEnVervolgBaseFinal,
    bariPostNurseSpecialistFollowUpComorbiditeitenBaseFinal,
    bariPostNurseSpecialistFollowUpLeefstijlEnMedicatieBaseFinal,
} from '../bariPostNurseSpecialistFollowUpSchemaBase';

export type BariPostNurseSpecialistFollowUpComorbiditeitenKeyV2 =
    keyof BariPostNurseSpecialistFollowUpComorbiditeitenDataV2;

function therapeutischeMedicamenteuzeVeranderingenSchemaV2(key: BariPostNurseSpecialistFollowUpComorbiditeitenKeyV2) {
    return Koi.when(key, {
        is: null,
        otherwise: Koi.when(key, {
            is: 'Niet van toepassing',
            otherwise: Koi.koi().enum(TherapeutischeMedicamenteuzeVeranderingOpties),
            then: Koi.valid(null),
        }),
        then: Koi.valid(null),
    });
}

function toelichtingenSchema(key: BariPostNurseSpecialistFollowUpComorbiditeitenKeyV2) {
    return Koi.when(key, {
        is: null,
        otherwise: Koi.when(key, {
            is: 'Geen verandering',
            otherwise: getTextAreaStringSchema(),
            then: Koi.valid(null),
        }),
        then: Koi.valid(null),
    });
}

const bariPostNurseSpecialistFollowUpLeefstijlEnMedicatieFinalV2: TypedSchema<BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataV2> =
    {
        ...bariPostNurseSpecialistFollowUpLeefstijlEnMedicatieBaseFinal,
        tabak_gebruik: Koi.koi().enum(TabakOpties),
        tabak_toelichting: getTextAreaStringSchema(true),
        toelichting_deficienties_labuitslag: getTextAreaStringSchema(true),
    };

const bariPostNurseSpecialistFollowUpComorbiditeitenFinalV2: TypedSchema<BariPostNurseSpecialistFollowUpComorbiditeitenDataV2> =
    {
        ...bariPostNurseSpecialistFollowUpComorbiditeitenBaseFinal,
        adjuvante_gewichtsreducerende_medicatie: Koi.koi()
            .enum(AdjuvanteGewichtsreducerendeMedicatieOpties)
            .allow(null),
        adjuvante_gewichtsreducerende_medicatie_startdatum: Koi.koi().date().allow(null),
        adjuvante_gewichtsreducerende_medicatie_stopdatum: Koi.koi().date().allow(null),
        depressie_status: Koi.koi().enum(FollowUpStatusOpties),
        diabetes_mellitus_medicamenteuze_veranderingen:
            therapeutischeMedicamenteuzeVeranderingenSchemaV2('diabetes_mellitus'),
        diabetes_mellitus_medicamenteuze_veranderingen_toelichting: toelichtingenSchema(
            'diabetes_mellitus_medicamenteuze_veranderingen'
        ),
        dyslipidemie_medicamenteuze_veranderingen: therapeutischeMedicamenteuzeVeranderingenSchemaV2('dyslipidemie'),
        dyslipidemie_medicamenteuze_veranderingen_toelichting: toelichtingenSchema(
            'dyslipidemie_medicamenteuze_veranderingen'
        ),
        gastroesophageal_reflux_disease_medicamenteuze_veranderingen: therapeutischeMedicamenteuzeVeranderingenSchemaV2(
            'gastroesophageal_reflux_disease'
        ),
        gastroesophageal_reflux_disease_medicamenteuze_veranderingen_toelichting: toelichtingenSchema(
            'gastroesophageal_reflux_disease_medicamenteuze_veranderingen'
        ),
        gewrichtsklachten_therapeutische_veranderingen:
            therapeutischeMedicamenteuzeVeranderingenSchemaV2('gewrichtsklachten'),
        gewrichtsklachten_therapeutische_veranderingen_toelichting: toelichtingenSchema(
            'gewrichtsklachten_therapeutische_veranderingen'
        ),
        hba1c: Koi.when('diabetes_mellitus', {
            is: null,
            otherwise: Koi.when('diabetes_mellitus', {
                is: 'Niet van toepassing',
                otherwise: hba1cSchema,
                then: hba1cSchema.allow(null),
            }),
            then: hba1cSchema.allow(null),
        }),
        hypertensie_medicamenteuze_veranderingen: therapeutischeMedicamenteuzeVeranderingenSchemaV2('hypertensie'),
        hypertensie_medicamenteuze_veranderingen_toelichting: toelichtingenSchema(
            'hypertensie_medicamenteuze_veranderingen'
        ),
        longziekte_status: Koi.koi().enum(FollowUpStatusOpties),
        osas_therapeutische_veranderingen: therapeutischeMedicamenteuzeVeranderingenSchemaV2('osas'),
        osas_therapeutische_veranderingen_toelichting: toelichtingenSchema('osas_therapeutische_veranderingen'),
        start_adjuvante_gewichtsreducerende_medicatie: Koi.boolean(),
    };

const bariPostNurseSpecialistFollowUpBeoordelingEnVervolgFinalV2 =
    bariPostNurseSpecialistFollowUpBeoordelingEnVervolgBaseFinal;

const bariPostNurseSpecialistFollowUpFinalV2: TypedSchema<BariPostNurseSpecialistFollowUpDataV2> = {
    ...bariPostNurseSpecialistFollowUpLeefstijlEnMedicatieFinalV2,
    ...bariPostNurseSpecialistFollowUpComorbiditeitenFinalV2,
    ...bariPostNurseSpecialistFollowUpBeoordelingEnVervolgFinalV2,
};

const bariPostNurseSpecialistFollowUpDraftV2: TypedSchema<BariPostNurseSpecialistFollowUpDataV2> = {
    ...addAllowNull(bariPostNurseSpecialistFollowUpFinalV2, ['assets', 'dumpingklachten_options']),
    assets: getProcessActivityAssetsSchema(),
    dumpingklachten_options: Koi.object({
        dumpingklachten_late_dumping: Koi.boolean().allow(null),
        dumpingklachten_vroege_dumping: Koi.boolean().allow(null),
    }),
};

export const bariPostNurseSpecialistFollowUpDraftSchemaV2 = Koi.object(bariPostNurseSpecialistFollowUpDraftV2);
export const bariPostNurseSpecialistFollowUpFinalSchemaV2 = Koi.object(bariPostNurseSpecialistFollowUpFinalV2);
